import { FC } from 'react';
import { WarningAmberRounded as WarningIcon } from '@mui/icons-material';
import { Box, LinearProgress, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { ConfirmationButton, ConfirmationModal } from '@components';
import { useDirection } from '@hooks';
import { PluralKeys, SingularKeys, Trans, useTrans } from '@libs/i18n';
import { CheckCircle as CheckIcon, CloudArrowUp as CloudIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

interface ExitAssessmentModalProps {
  open: boolean;
  onCancel: () => void;
  onSaveAndExit: () => Promise<void>;
  onDeleteAndExit: () => Promise<void>;
  isLoading?: boolean;
  completionPercentage: number;
  completedDomainsCount: number;
  totalDomains: number;
  filesAttachedCount: number;
}

/**
 * Modal shown when user clicks "Exit Assessment"
 * Displays progress statistics and provides 3 options:
 * 1. Cancel - Stay in the domain
 * 2. Save Progress & Exit - Save current domain then navigate to assessments list
 * 3. Delete all & Exit - Delete the entire assessment then navigate to assessments list
 */
export const ExitAssessmentModal: FC<ExitAssessmentModalProps> = observer(({
  open,
  onCancel,
  onSaveAndExit,
  onDeleteAndExit,
  isLoading = false,
  completionPercentage,
  completedDomainsCount,
  totalDomains,
  filesAttachedCount,
}) => {
  const { t } = useTrans();
  const { dir } = useDirection();

  const buttons: ConfirmationButton[] = [
    {
      label: (
        <Trans
          i18nKey={'assessment.workflow.exitAssessment.cancel' as SingularKeys}
          defaults="Cancel"
        />
      ),
      onClick: onCancel,
      variant: 'outlined',
      sx: {
        border: '1px none #000000',
        boxShadow: 'none',
        paddingInlineEnd: '70px',
      },
      disabled: isLoading,
    },
    {
      label: (
        <Trans
          i18nKey={'assessment.workflow.exitAssessment.saveAndExit' as SingularKeys}
          defaults="Save Progress & Exit"
        />
      ),
      onClick: onSaveAndExit,
      variant: 'contained',
      sx: {
        backgroundColor: 'grey.200',
        color: '#000000',
        '&:hover': {
          backgroundColor: 'grey.300',
        },
        '&:disabled': {
          backgroundColor: 'grey.100',
          color: 'text.secondary',
        },
        border: '1px none #000000',
        boxShadow: 'none',
      },
      isLoading: isLoading,
      disabled: isLoading,
    },
    {
      label: (
        <Trans
          i18nKey={'assessment.workflow.exitAssessment.deleteAndExit' as SingularKeys}
          defaults="Delete all & Exit"
        />
      ),
      onClick: onDeleteAndExit,
      variant: 'contained',
      sx: {
        backgroundColor: 'error.main',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: 'error.dark',
        },
        '&:disabled': {
          backgroundColor: 'error.light',
          opacity: 0.7,
        },
        border: '1px none #000000',
        boxShadow: 'none',
      },
      isLoading: isLoading,
      disabled: isLoading,
    },
  ];

  return (
    <ConfirmationModal
      open={open}
      onClose={onCancel}
      title={
        <Trans
          i18nKey={'assessment.workflow.exitAssessment.title' as SingularKeys}
          defaults="Exit Assessment?"
        />
      }
      icon={<WarningIcon sx={{ fontSize: 24 }} />}
      iconColor="warning"
      buttons={buttons}
      isLoading={isLoading}
      content={
        <>
          {/* Main Message */}
          <Typography variant="body1" color="text.primary" sx={{ mb: 3 }}>
            <Trans
              i18nKey={'assessment.workflow.exitAssessment.message' as SingularKeys}
              defaults="You have progress that can be saved before leaving."
            />
          </Typography>

          {/* Progress Statistics */}
          <Box sx={{ mb: 3 }}>
            {/* Overall Progress */}
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight={500} color="text.primary">
                  <Trans
                    i18nKey={'assessment.workflow.exitAssessment.overallProgress' as SingularKeys}
                    defaults="Overall Progress"
                  />
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.primary">
                  {completionPercentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            </Box>

            {/* Domains Completed */}
            <NoFlipBox
              mode={dir}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                py: 1,
                backgroundColor: 'grey.200',
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid #D6D6D6',
                boxShadow: 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'success.lighter',
                    color: 'success.main',
                  }}
                >
                  <CheckIcon size={18} weight="fill" />
                </Box>
                <Typography variant="body2" fontWeight={500} color="text.primary">
                  <Trans
                    i18nKey={'assessment.workflow.exitAssessment.domainsCompleted' as SingularKeys}
                  />
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {t('assessment.workflow.exitAssessment.domainsCount' as SingularKeys, { completed: completedDomainsCount, total: totalDomains })}
              </Typography>
            </NoFlipBox>

            {/* Evidence Uploaded */}
            <NoFlipBox
              mode={dir}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                backgroundColor: 'grey.200',
                padding: '10px',
                borderRadius: '10px',
                border: '1px solid #D6D6D6',
                boxShadow: 'none',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: 'primary.lighter',
                    color: 'primary.main',
                  }}
                >
                  <CloudIcon size={18} weight="fill" />
                </Box>
                <Typography variant="body2" fontWeight={500} color="text.primary">
                  <Trans
                    i18nKey={'assessment.workflow.exitAssessment.evidenceUploaded' as SingularKeys}
                  />
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {t('assessment.workflow.exitAssessment.filesAttached' as PluralKeys, { count: filesAttachedCount })}
              </Typography>
            </NoFlipBox>
          </Box>

          {/* Secondary Message */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <Trans
              i18nKey={'assessment.workflow.exitAssessment.helpText' as SingularKeys}
            />
          </Typography>
        </>
      }
    />
  );
});
